import { usePageRefreshKey } from '../../../context/PageRefreshContext';
import { useState, useMemo, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getVendorPaymentsList, getAllVendors, deleteVendorPayment, bulkUploadVendorPayments, resetVendorPaymentBulkUpload } from '../../../redux/actions/SocietyPortal/VendorAction/VendorAction';
import Pagination from '../../../components/common/Pagination';
import * as routes from '../../../Routing/routes';
import { selectedSocietyStorer } from '../../../utils/webStorer';
import axiosInstance from '../../../axios/axiosInstance';
import apiEndpoints from '../../../axios/api';
import { notify } from '../../../utils/notification';
import { getPaymentTypeLabel } from '../../../utils/helpers/paymentType';
import { canAny } from '../../../utils/helpers/can';
import { PERMISSIONS } from '../../../consts/permissions';

const PAYMENT_MODES = ['Cash', 'Cheque', 'NEFT'];

// vendor_payments.generate_status is the enum ('Draft' | 'Post'). The list used to test for
// 'posted'/'completed', which nothing ever equals — so every payment read as a draft.
const isPosted = (status) => String(status || '').toLowerCase() === 'post';
const isDraft = (status) => String(status || '').toLowerCase() === 'draft';

const DraftPaymentCheckbox = ({ checked, onChange, label, className = '' }) => (
    <span className={`tw-relative tw-inline-flex tw-h-5 tw-w-5 tw-flex-shrink-0 ${className}`}>
        <input
            type="checkbox"
            className="tw-absolute tw-inset-0 tw-z-10 tw-h-5 tw-w-5 tw-cursor-pointer tw-opacity-0"
            checked={checked}
            onChange={onChange}
            aria-label={label}
        />
        <span
            aria-hidden="true"
            className={`tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded tw-border-2 tw-transition-colors ${
                checked
                    ? 'tw-border-emerald-700 tw-bg-emerald-600 tw-text-white'
                    : 'tw-border-gray-600 tw-bg-gray-100'
            }`}
        >
            {checked && <Icon icon="mdi:check-bold" className="tw-text-sm" />}
        </span>
    </span>
);

const VendorPayment = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [postingId, setPostingId] = useState(null);

    const {
        vendorPaymentsListData,
        vendorPaymentsListLoading,
        vendorPaymentsPagination,
        allVendorsData,
        allVendorsLoading,
        deleteVendorPaymentLoading,
        // Bulk Excel upload
        bulkUploadVendorPaymentsLoading,
        bulkUploadVendorPaymentsResult,
    } = useSelector(state => state.VendorReducers);
    const userPermissions = useSelector(state => state.UserSocietyReducer?.permissions || []);
    const canCreatePayments = canAny(userPermissions, [PERMISSIONS.VENDOR_PAYMENT_CREATE]);

    const [searchQuery, setSearchQuery] = useState('');
    const [filterVendor, setFilterVendor] = useState('');
    const [filterMode, setFilterMode] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [viewMode, setViewMode] = useState('list');
    const [deleteModal, setDeleteModal] = useState({ show: false, payment: null });

    // Bulk Excel upload — picker + results modals, mirroring VendorBill.jsx.
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadResultOpen, setUploadResultOpen] = useState(false);

    const handleDownloadPaymentTemplate = async () => {
        try {
            const res = await axiosInstance.get(apiEndpoints.SOCIETY_VENDOR_PAYMENT_UPLOAD_TEMPLATE_API, { responseType: 'blob' });
            const url = URL.createObjectURL(new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = 'vendor-payment-upload-template.xlsx';
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            window.open(apiEndpoints.SOCIETY_VENDOR_PAYMENT_UPLOAD_TEMPLATE_API, '_blank');
        }
    };

    const handleUploadPaymentSubmit = () => {
        if (!uploadFile) return;
        const societyId = selectedSocietyStorer.getSocietyId();
        dispatch(bulkUploadVendorPayments({
            file: uploadFile,
            societyId,
            onSuccess: () => {
                setUploadModalOpen(false);
                setUploadFile(null);
                setUploadResultOpen(true);
                dispatch(getVendorPaymentsList({
                    societyId,
                    page: currentPage,
                    limit: itemsPerPage
                }));
            }
        }));
    };

    const refreshPayments = () => {
        dispatch(getVendorPaymentsList({
            societyId: selectedSocietyStorer.getSocietyId(),
            page: currentPage,
            limit: itemsPerPage,
            search: searchQuery,
            vendor_id: filterVendor,
            payment_mode: filterMode,
        }));
    };

    // Promote a draft to Post — the backend writes the journal entry on this transition.
    const handlePostPayment = async (payment) => {
        setPostingId(payment.id);
        try {
            const url = apiEndpoints.SOCIETY_UPDATE_VENDOR_PAYMENT_API.replace(':id', payment.id);
            const res = await axiosInstance.put(url, { generate_status: 'Post' });
            if (res?.success) {
                notify('success', res.message || 'Payment posted');
                refreshPayments();
            } else {
                notify('error', res?.message || 'Could not post the payment');
            }
        } catch (err) {
            notify('error', err.response?.data?.message || 'Could not post the payment');
        } finally {
            setPostingId(null);
        }
    };

    const handleClosePaymentResults = () => {
        setUploadResultOpen(false);
        dispatch(resetVendorPaymentBulkUpload());
    };

    useEffect(() => {
        dispatch(getAllVendors({ society_id: selectedSocietyStorer.getSocietyId() }));
    }, [dispatch]);

    const refreshKey = usePageRefreshKey();
    useEffect(() => {
        dispatch(getVendorPaymentsList({
            societyId: selectedSocietyStorer.getSocietyId(),
            page: currentPage,
            limit: itemsPerPage,
            search: searchQuery,
            vendor_id: filterVendor,
            payment_mode: filterMode,
        }));
    }, [dispatch, currentPage, itemsPerPage, searchQuery, filterVendor, filterMode, refreshKey]);

    const payments = useMemo(() => {
        if (!Array.isArray(vendorPaymentsListData)) return [];
        return vendorPaymentsListData.map(p => ({
            id: p.payment_id || p.id,
            paymentNo: p.payment_no || '-',
            vendorName: p.vendor_name || p.vendor?.business_name || '-',
            paymentType: p.payment_type || '',
            amount: parseFloat(p.payment_amount || 0),
            paymentDate: p.payment_date,
            paymentMode: p.payment_mode,
            referenceNo: p.reference || '',
            status: p.generate_status || 'Draft',
            tdsApplicable: p.tds_applicable || 'N',
            tdsRate: parseFloat(p.tds_rate) || 0,
            tdsAmount: parseFloat(p.tds_amount) || 0,
            tdsSection: p.tds_section || '',
            notes: p.notes || '',
            details: p.details || [],
        }));
    }, [vendorPaymentsListData]);

    const vendorsList = useMemo(() => {
        if (!Array.isArray(allVendorsData)) return [];
        return allVendorsData.map(v => ({ id: v.vendor_id, name: v.business_name }));
    }, [allVendorsData]);

    const stats = useMemo(() => {
        const totalAmount = payments.reduce((sum, p) => sum + p.amount, 0);
        const postedCount = payments.filter(p => isPosted(p.status)).length;
        const draftCount = payments.filter(p => p.status?.toLowerCase() === 'draft').length;
        return {
            total: payments.length,
            totalAmount,
            postedCount,
            draftCount,
        };
    }, [payments]);

    // Draft payments ticked for bulk posting, by payment id.
    const [selectedIds, setSelectedIds] = useState([]);
    const [bulkPosting, setBulkPosting] = useState(false);

    const draftIdsOnPage = useMemo(
        () => payments.filter(p => isDraft(p.status)).map(p => p.id),
        [payments]
    );
    const selectedDraftIds = useMemo(
        () => selectedIds.filter(id => draftIdsOnPage.includes(id)),
        [selectedIds, draftIdsOnPage]
    );

    const toggleSelected = (id) => {
        setSelectedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
    };
    const toggleSelectAll = () => {
        setSelectedIds(prev => (selectedDraftIds.length === draftIdsOnPage.length ? [] : draftIdsOnPage));
    };

    // Post every ticked draft in one request; the response reports each failure.
    const handleBulkPost = async () => {
        if (selectedDraftIds.length === 0) return;
        setBulkPosting(true);
        try {
            const res = await axiosInstance.put(apiEndpoints.SOCIETY_BULK_POST_VENDOR_PAYMENTS_API, {
                payment_ids: selectedDraftIds,
            });
            if (res?.success) {
                const { postedCount = 0, failed = [] } = res.data || {};
                if (postedCount > 0) notify('success', res.message || `${postedCount} payment(s) posted`);
                failed.forEach(f => notify('error', `${f.payment_no || `Payment ${f.payment_id}`}: ${f.error}`));
                setSelectedIds([]);
                refreshPayments();
            } else {
                notify('error', res?.message || 'Could not post the selected payments');
            }
        } catch (err) {
            notify('error', err.response?.data?.message || 'Could not post the selected payments');
        } finally {
            setBulkPosting(false);
        }
    };


    const totalPages = vendorPaymentsPagination?.totalPages || 1;
    const totalItems = vendorPaymentsPagination?.total || payments.length;

    const clearFilters = () => {
        setSearchQuery('');
        setFilterVendor('');
        setFilterMode('');
        setCurrentPage(1);
    };

    const hasActiveFilters = searchQuery || filterVendor || filterMode;

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
    };

    const getStatusBadge = (status) => {
        const s = status?.toLowerCase();
        if (isPosted(status)) return 'tw-bg-emerald-100 tw-text-emerald-700';
        if (s === 'draft') return 'tw-bg-amber-100 tw-text-amber-700';
        if (s === 'failed' || s === 'cancelled') return 'tw-bg-red-100 tw-text-red-700';
        return 'tw-bg-gray-100 tw-text-gray-600';
    };

    const getPaymentModeIcon = (mode) => {
        const icons = { cash: 'mdi:cash', Cash: 'mdi:cash', cheque: 'mdi:checkbook', Cheque: 'mdi:checkbook', neft: 'mdi:bank-outline', NEFT: 'mdi:bank-outline' };
        return icons[mode] || 'mdi:credit-card';
    };

    const getPaymentModeLabel = (mode) => {
        const labels = { cash: 'Cash', cheque: 'Cheque', neft: 'NEFT' };
        return labels[mode] || mode;
    };

    const confirmDeletePayment = () => {
        if (!deleteModal.payment) return;
        dispatch(deleteVendorPayment({
            paymentId: deleteModal.payment.id,
            onSuccess: () => {
                setDeleteModal({ show: false, payment: null });
                dispatch(getVendorPaymentsList({
                    societyId: selectedSocietyStorer.getSocietyId(),
                    page: currentPage,
                    limit: itemsPerPage,
                    search: searchQuery,
                    vendor_id: filterVendor,
                    payment_mode: filterMode,
                }));
            },
        }));
    };

    return (
        <>
            {/* Header */}
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                <div>
                    <h1 className="tw-text-lg tw-font-semibold tw-text-gray-800">Vendor Payments</h1>
                    <p className="tw-text-xs tw-text-gray-500">Track and manage vendor payment transactions</p>
                </div>
                <div className="tw-flex tw-items-center tw-gap-2">
                    {canCreatePayments && (
                        <button
                            onClick={() => setUploadModalOpen(true)}
                            className="tw-flex tw-items-center tw-gap-1.5 tw-bg-white tw-text-blue-700 tw-border tw-border-blue-200 tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm tw-font-medium hover:tw-bg-blue-50"
                            title="Bulk upload vendor payments from Excel"
                        >
                            <Icon icon="mdi:file-excel-outline" /> Bulk Upload Payments
                        </button>
                    )}
                    <button
                        onClick={() => navigate('/society-portal' + routes.SOCIETY_PORTAL_VENDOR_PAYMENTS_ADD)}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-bg-blue-600 tw-text-white tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm tw-font-medium hover:tw-bg-blue-700"
                    >
                        <Icon icon="mdi:plus" /> Record Payment
                    </button>
                </div>
            </div>

            {/* Keep draft selection controls visible even before a payment is selected. */}
            {draftIdsOnPage.length > 0 && (
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-mb-3 tw-px-3 tw-py-2 tw-bg-emerald-50 tw-border tw-border-emerald-200 tw-rounded-lg">
                    <div className="tw-flex tw-items-center tw-gap-3">
                        <label className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-text-xs tw-font-medium tw-text-emerald-900">
                            <DraftPaymentCheckbox
                                checked={selectedDraftIds.length === draftIdsOnPage.length}
                                onChange={toggleSelectAll}
                                label="Select all draft payments on this page"
                            />
                            Select all draft payments
                        </label>
                        <span className="tw-text-xs tw-text-emerald-800">
                            {selectedDraftIds.length} of {draftIdsOnPage.length} selected
                        </span>
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <button
                            onClick={() => setSelectedIds([])}
                            disabled={selectedDraftIds.length === 0}
                            className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-600 tw-bg-white tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50 disabled:tw-opacity-50"
                        >
                            Clear
                        </button>
                        <button
                            onClick={handleBulkPost}
                            disabled={bulkPosting || selectedDraftIds.length === 0}
                            className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-emerald-600 tw-rounded-md hover:tw-bg-emerald-700 disabled:tw-opacity-50"
                        >
                            <Icon icon={bulkPosting ? 'mdi:loading' : 'mdi:check-circle-outline'} className={bulkPosting ? 'tw-animate-spin' : ''} />
                            {bulkPosting ? 'Posting…' : `Post selected (${selectedDraftIds.length})`}
                        </button>
                    </div>
                </div>
            )}

            {/* Stats Cards */}
            <div className="tw-grid tw-grid-cols-3 tw-gap-3 tw-mb-4">
                {[
                    { icon: 'mdi:cash-multiple', color: 'tw-bg-blue-500', value: stats.total, label: 'Total Payments', sub: formatCurrency(stats.totalAmount) },
                    { icon: 'mdi:check-circle', color: 'tw-bg-emerald-500', value: stats.postedCount, label: 'Posted' },
                    { icon: 'mdi:file-edit-outline', color: 'tw-bg-amber-500', value: stats.draftCount, label: 'Draft' },
                ].map((stat, i) => (
                    <div key={i} className="tw-bg-white tw-rounded-lg tw-p-3 tw-shadow-sm tw-border tw-border-gray-100 tw-flex tw-items-center tw-gap-2.5">
                        <div className={`tw-w-10 tw-h-10 tw-rounded-lg ${stat.color} tw-flex tw-items-center tw-justify-center tw-flex-shrink-0`}>
                            <Icon icon={stat.icon} className="tw-text-white tw-text-lg" />
                        </div>
                        <div>
                            <p className="tw-text-base tw-font-bold tw-text-gray-800 tw-leading-none">{stat.value}</p>
                            <p className="tw-text-[11px] tw-text-gray-500">{stat.label}</p>
                            {stat.sub && <p className="tw-text-[10px] tw-text-gray-400">{stat.sub}</p>}
                        </div>
                    </div>
                ))}
            </div>

            {/* Filter Bar */}
            <div className="tw-bg-white tw-rounded-lg tw-py-2.5 tw-px-3 tw-mb-4 tw-shadow-sm tw-border tw-border-gray-100 tw-flex tw-items-center tw-gap-2.5 tw-flex-wrap">
                <div className="tw-flex-1 tw-min-w-[200px] tw-relative">
                    <Icon icon="mdi:magnify" className="tw-absolute tw-left-2.5 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-text-sm" />
                    <input
                        type="text"
                        placeholder="Search by payment no, vendor, or reference..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="tw-w-full tw-pl-8 tw-pr-2 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-border-blue-500"
                    />
                </div>
                <select
                    value={filterVendor}
                    onChange={(e) => { setFilterVendor(e.target.value); setCurrentPage(1); }}
                    disabled={allVendorsLoading}
                    className="tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-blue-500 disabled:tw-bg-gray-100 disabled:tw-cursor-not-allowed"
                >
                    <option value="">{allVendorsLoading ? 'Loading...' : 'All Vendors'}</option>
                    {vendorsList.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
                <select value={filterMode} onChange={(e) => { setFilterMode(e.target.value); setCurrentPage(1); }} className="tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-blue-500">
                    <option value="">All Payment Modes</option>
                    {PAYMENT_MODES.map(m => <option key={m} value={m.toLowerCase()}>{m}</option>)}
                </select>
                {hasActiveFilters && (
                    <button onClick={clearFilters} className="tw-flex tw-items-center tw-gap-1 tw-text-red-500 tw-text-xs tw-font-medium hover:tw-text-red-600">
                        <Icon icon="mdi:close" className="tw-text-sm" /> Clear
                    </button>
                )}
                <div className="tw-flex tw-border tw-border-gray-200 tw-rounded-md tw-overflow-hidden">
                    <button onClick={() => setViewMode('grid')} className={`tw-p-1.5 ${viewMode === 'grid' ? 'tw-bg-blue-600 tw-text-white' : 'tw-bg-white tw-text-gray-500'}`}>
                        <Icon icon="mdi:view-grid" className="tw-text-sm" />
                    </button>
                    <button onClick={() => setViewMode('list')} className={`tw-p-1.5 ${viewMode === 'list' ? 'tw-bg-blue-600 tw-text-white' : 'tw-bg-white tw-text-gray-500'}`}>
                        <Icon icon="mdi:view-list" className="tw-text-sm" />
                    </button>
                </div>
                <span className="tw-text-[11px] tw-text-gray-400 tw-ml-auto">
                    {vendorPaymentsListLoading ? 'Loading...' : `Showing ${payments.length} of ${totalItems} payments`}
                </span>
            </div>

            {/* Loading State */}
            {vendorPaymentsListLoading && (
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-text-center tw-py-12">
                    <Icon icon="mdi:loading" className="tw-text-4xl tw-text-blue-500 tw-animate-spin" />
                    <p className="tw-text-gray-500 tw-mt-2 tw-text-sm">Loading payments...</p>
                </div>
            )}

            {/* Grid View */}
            {!vendorPaymentsListLoading && viewMode === 'grid' && payments.length > 0 && (
                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 tw-gap-3">
                    {payments.map((payment) => (
                        <div key={payment.id} className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-p-3">
                            {/* Header: Payment No & Status */}
                            <div className="tw-flex tw-items-start tw-justify-between tw-mb-2.5">
                                <div className="tw-flex tw-items-center tw-gap-2.5">
                                    <div className="tw-w-10 tw-h-10 tw-rounded-lg tw-bg-blue-500 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                        <Icon icon="mdi:cash-check" className="tw-text-white tw-text-lg" />
                                    </div>
                                    <div>
                                        <p className="tw-font-semibold tw-text-gray-800 tw-text-sm">{payment.paymentNo}</p>
                                        <p className="tw-text-[11px] tw-text-gray-500">{payment.vendorName}</p>
                                    </div>
                                </div>
                                <div className="tw-flex tw-items-center tw-gap-2">
                                    {isDraft(payment.status) && (
                                        <DraftPaymentCheckbox
                                            checked={selectedIds.includes(payment.id)}
                                            onChange={() => toggleSelected(payment.id)}
                                            label={`Select draft payment ${payment.paymentNo}`}
                                        />
                                    )}
                                    <span className={`tw-px-2 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium ${getStatusBadge(payment.status)}`}>
                                        {payment.status}
                                    </span>
                                </div>
                            </div>

                            {/* Type */}
                            <p className="tw-text-xs tw-text-gray-600 tw-mb-2">{getPaymentTypeLabel(payment.paymentType)}</p>

                            {/* Amount */}
                            <div className="tw-py-2 tw-border-t tw-border-b tw-border-gray-100 tw-mb-2">
                                <div className="tw-flex tw-items-center tw-justify-between">
                                    <span className="tw-text-xs tw-text-gray-500">Amount Paid</span>
                                    <span className="tw-font-bold tw-text-gray-800 tw-text-sm">{formatCurrency(payment.amount)}</span>
                                </div>
                                {payment.tdsAmount > 0 && (
                                    <div className="tw-flex tw-items-center tw-justify-between tw-mt-1">
                                        <span className="tw-text-xs tw-text-gray-500">
                                            TDS ({payment.tdsRate}%{payment.tdsSection ? ` · ${payment.tdsSection}` : ''})
                                        </span>
                                        <span className="tw-text-xs tw-font-semibold tw-text-orange-600">{formatCurrency(payment.tdsAmount)}</span>
                                    </div>
                                )}
                                {payment.tdsAmount > 0 && (
                                    <div className="tw-flex tw-items-center tw-justify-between tw-mt-1">
                                        <span className="tw-text-xs tw-text-gray-500">Net Paid</span>
                                        <span className="tw-text-xs tw-font-semibold tw-text-gray-800">
                                            {formatCurrency((payment.amount || 0) - (payment.tdsAmount || 0))}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Details */}
                            <div className="tw-space-y-1.5 tw-mb-2">
                                <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-gray-600">
                                    <Icon icon="mdi:calendar-outline" className="tw-text-gray-400 tw-text-sm" />
                                    <span>{payment.paymentDate ? new Date(payment.paymentDate).toLocaleDateString('en-IN') : '-'}</span>
                                </div>
                                <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-gray-600">
                                    <Icon icon={getPaymentModeIcon(payment.paymentMode)} className="tw-text-gray-400 tw-text-sm" />
                                    <span>{getPaymentModeLabel(payment.paymentMode)}</span>
                                    {payment.referenceNo && <span className="tw-text-gray-400 tw-font-mono tw-text-[10px]">({payment.referenceNo})</span>}
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="tw-flex tw-items-center tw-justify-end tw-gap-1 tw-pt-2 tw-border-t tw-border-gray-100">
                                <button onClick={() => navigate('/society-portal' + routes.SOCIETY_PORTAL_VENDOR_PAYMENTS_VIEW.replace(':id', payment.id))} className="tw-p-1.5 tw-rounded hover:tw-bg-blue-50 tw-text-gray-400 hover:tw-text-blue-600" title="View">
                                    <Icon icon="mdi:eye-outline" />
                                </button>
                                <button onClick={() => navigate('/society-portal' + routes.SOCIETY_PORTAL_VENDOR_PAYMENTS_EDIT.replace(':id', payment.id))} className="tw-p-1.5 tw-rounded hover:tw-bg-gray-100 tw-text-gray-400 hover:tw-text-gray-600" title="Edit">
                                    <Icon icon="mdi:pencil-outline" />
                                </button>
                                <button onClick={() => navigate('/society-portal' + routes.SOCIETY_PORTAL_VENDOR_PAYMENTS_VIEW.replace(':id', payment.id) + '?print=true')} className="tw-p-1.5 tw-rounded hover:tw-bg-gray-100 tw-text-gray-400 hover:tw-text-gray-600" title="Print Receipt">
                                    <Icon icon="mdi:printer-outline" />
                                </button>
                                {!isPosted(payment.status) && (
                                    <button
                                        onClick={() => handlePostPayment(payment)}
                                        disabled={postingId === payment.id}
                                        className="tw-p-1.5 tw-rounded hover:tw-bg-emerald-50 tw-text-gray-400 hover:tw-text-emerald-600 disabled:tw-opacity-50"
                                        title="Post to ledger"
                                    >
                                        <Icon icon={postingId === payment.id ? 'mdi:loading' : 'mdi:check-decagram-outline'} className={postingId === payment.id ? 'tw-animate-spin' : ''} />
                                    </button>
                                )}
                                <button onClick={() => setDeleteModal({ show: true, payment })} className="tw-p-1.5 tw-rounded hover:tw-bg-red-50 tw-text-gray-400 hover:tw-text-red-600" title="Delete">
                                    <Icon icon="mdi:delete-outline" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Grid View Empty State */}
            {!vendorPaymentsListLoading && viewMode === 'grid' && payments.length === 0 && (
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-text-center tw-py-12">
                    <Icon icon="mdi:cash-off" className="tw-text-4xl tw-text-gray-300" />
                    <p className="tw-text-gray-500 tw-mt-2 tw-text-sm">No payments found</p>
                </div>
            )}

            {/* List View */}
            {!vendorPaymentsListLoading && viewMode === 'list' && (
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-overflow-hidden">
                    <table className="tw-w-full tw-text-sm">
                        <thead>
                            <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-100">
                                <th className="tw-px-3 tw-py-2 tw-w-16 tw-text-left tw-text-[10px] tw-font-semibold tw-text-gray-500 tw-uppercase">
                                    Select
                                </th>
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Payment No</th>
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Vendor</th>
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Type</th>
                                <th className="tw-text-right tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Amount</th>
                                <th className="tw-text-right tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">TDS</th>
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Payment Date</th>
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Mode</th>
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Status</th>
                                <th className="tw-text-center tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {payments.map((payment) => (
                                <tr key={payment.id} className="tw-border-b tw-border-gray-50 hover:tw-bg-gray-50">
                                    <td className="tw-px-3 tw-py-2">
                                        {isDraft(payment.status) && (
                                            <DraftPaymentCheckbox
                                                checked={selectedIds.includes(payment.id)}
                                                onChange={() => toggleSelected(payment.id)}
                                                label={`Select draft payment ${payment.paymentNo}`}
                                            />
                                        )}
                                    </td>
                                    <td className="tw-px-3 tw-py-2">
                                        <span className="tw-font-medium tw-text-blue-600">{payment.paymentNo}</span>
                                    </td>
                                    <td className="tw-px-3 tw-py-2">
                                        <p className="tw-font-medium tw-text-gray-800">{payment.vendorName}</p>
                                    </td>
                                    <td className="tw-px-3 tw-py-2">
                                        <span className="tw-text-gray-600">{getPaymentTypeLabel(payment.paymentType)}</span>
                                    </td>
                                    <td className="tw-px-3 tw-py-2 tw-text-right">
                                        <p className="tw-font-semibold tw-text-gray-800">{formatCurrency(payment.amount)}</p>
                                    </td>
                                    <td className="tw-px-3 tw-py-2 tw-text-right">
                                        {payment.tdsAmount > 0 ? (
                                            <>
                                                <p className="tw-font-medium tw-text-orange-600">{formatCurrency(payment.tdsAmount)}</p>
                                                <p className="tw-text-[10px] tw-text-gray-500">
                                                    {payment.tdsRate}%{payment.tdsSection ? ` · ${payment.tdsSection}` : ''}
                                                </p>
                                            </>
                                        ) : (
                                            <span className="tw-text-gray-400">-</span>
                                        )}
                                    </td>
                                    <td className="tw-px-3 tw-py-2 tw-text-gray-600">
                                        {payment.paymentDate ? new Date(payment.paymentDate).toLocaleDateString('en-IN') : '-'}
                                    </td>
                                    <td className="tw-px-3 tw-py-2">
                                        <div className="tw-flex tw-items-center tw-gap-1.5">
                                            <Icon icon={getPaymentModeIcon(payment.paymentMode)} className="tw-text-gray-400" />
                                            <span className="tw-text-gray-600">{getPaymentModeLabel(payment.paymentMode)}</span>
                                        </div>
                                    </td>
                                    <td className="tw-px-3 tw-py-2">
                                        <span className={`tw-px-2 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium ${getStatusBadge(payment.status)}`}>
                                            {payment.status}
                                        </span>
                                    </td>
                                    <td className="tw-px-3 tw-py-2">
                                        <div className="tw-flex tw-items-center tw-justify-center tw-gap-1">
                                            <button onClick={() => navigate('/society-portal' + routes.SOCIETY_PORTAL_VENDOR_PAYMENTS_VIEW.replace(':id', payment.id))} className="tw-p-1.5 tw-rounded hover:tw-bg-blue-50 tw-text-gray-400 hover:tw-text-blue-600" title="View">
                                                <Icon icon="mdi:eye-outline" />
                                            </button>
                                            <button onClick={() => navigate('/society-portal' + routes.SOCIETY_PORTAL_VENDOR_PAYMENTS_EDIT.replace(':id', payment.id))} className="tw-p-1.5 tw-rounded hover:tw-bg-gray-100 tw-text-gray-400 hover:tw-text-gray-600" title="Edit">
                                                <Icon icon="mdi:pencil-outline" />
                                            </button>
                                            <button onClick={() => navigate('/society-portal' + routes.SOCIETY_PORTAL_VENDOR_PAYMENTS_VIEW.replace(':id', payment.id) + '?print=true')} className="tw-p-1.5 tw-rounded hover:tw-bg-gray-100 tw-text-gray-400 hover:tw-text-gray-600" title="Print Receipt">
                                                <Icon icon="mdi:printer-outline" />
                                            </button>
                                            {!isPosted(payment.status) && (
                                                <button
                                                    onClick={() => handlePostPayment(payment)}
                                                    disabled={postingId === payment.id}
                                                    className="tw-p-1.5 tw-rounded hover:tw-bg-emerald-50 tw-text-gray-400 hover:tw-text-emerald-600 disabled:tw-opacity-50"
                                                    title="Post to ledger"
                                                >
                                                    <Icon icon={postingId === payment.id ? 'mdi:loading' : 'mdi:check-decagram-outline'} className={postingId === payment.id ? 'tw-animate-spin' : ''} />
                                                </button>
                                            )}
                                            <button onClick={() => setDeleteModal({ show: true, payment })} className="tw-p-1.5 tw-rounded hover:tw-bg-red-50 tw-text-gray-400 hover:tw-text-red-600" title="Delete">
                                                <Icon icon="mdi:delete-outline" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {payments.length === 0 && (
                        <div className="tw-text-center tw-py-8">
                            <Icon icon="mdi:cash-off" className="tw-text-3xl tw-text-gray-300" />
                            <p className="tw-text-gray-500 tw-mt-2 tw-text-sm">No payments found</p>
                        </div>
                    )}
                </div>
            )}

            {/* Pagination */}
            {totalItems > 0 && (
                <div className="tw-mt-4">
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalItems={totalItems}
                        itemsPerPage={itemsPerPage}
                        onPageChange={setCurrentPage}
                        onItemsPerPageChange={(value) => {
                            setItemsPerPage(value);
                            setCurrentPage(1);
                        }}
                        itemsPerPageOptions={[5, 10, 20, 50]}
                        itemLabel="payments"
                    />
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteModal.show && (
                <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50">
                    <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-p-6 tw-max-w-md tw-w-full tw-mx-4">
                        <div className="tw-flex tw-items-center tw-gap-3 tw-mb-4">
                            <div className="tw-w-10 tw-h-10 tw-rounded-full tw-bg-red-100 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                <Icon icon="mdi:delete-alert" className="tw-text-red-600 tw-text-xl" />
                            </div>
                            <div>
                                <h3 className="tw-text-lg tw-font-semibold tw-text-gray-800">Delete Payment</h3>
                                <p className="tw-text-sm tw-text-gray-500">This action cannot be undone</p>
                            </div>
                        </div>
                        <p className="tw-text-sm tw-text-gray-600 tw-mb-6">
                            Are you sure you want to delete payment <span className="tw-font-semibold">{deleteModal.payment?.paymentNo}</span>?
                        </p>
                        <div className="tw-flex tw-items-center tw-justify-end tw-gap-3">
                            <button
                                onClick={() => setDeleteModal({ show: false, payment: null })}
                                disabled={deleteVendorPaymentLoading}
                                className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-600 tw-bg-gray-100 hover:tw-bg-gray-200 tw-rounded-lg disabled:tw-opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDeletePayment}
                                disabled={deleteVendorPaymentLoading}
                                className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-red-600 hover:tw-bg-red-700 tw-rounded-lg tw-flex tw-items-center tw-gap-2 disabled:tw-opacity-50"
                            >
                                {deleteVendorPaymentLoading ? (
                                    <>
                                        <Icon icon="mdi:loading" className="tw-animate-spin" />
                                        Deleting...
                                    </>
                                ) : (
                                    <>
                                        <Icon icon="mdi:delete" />
                                        Delete
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Excel Upload — picker modal (payments) */}
            {uploadModalOpen && (
                <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4">
                    <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-w-full tw-max-w-md">
                        <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-border-b tw-border-gray-100">
                            <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-flex tw-items-center tw-gap-2">
                                <Icon icon="mdi:file-excel-outline" className="tw-text-emerald-600" />
                                Bulk Upload Vendor Payments
                            </h3>
                            <button
                                onClick={() => { setUploadModalOpen(false); setUploadFile(null); }}
                                className="tw-p-1 tw-rounded hover:tw-bg-gray-100 tw-text-gray-500"
                            >
                                <Icon icon="mdi:close" />
                            </button>
                        </div>
                        <div className="tw-p-4 tw-space-y-3">
                            <p className="tw-text-xs tw-text-gray-600">
                                One row per payment. Vendors are matched by Company Name.
                                Bill Number is optional — supply it to allocate the payment to a specific bill.
                            </p>
                            <button
                                type="button"
                                onClick={handleDownloadPaymentTemplate}
                                className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-text-blue-700 hover:tw-underline"
                            >
                                <Icon icon="mdi:download" /> Download template
                            </button>
                            <label className="tw-block">
                                <span className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Excel file</span>
                                <input
                                    type="file"
                                    accept=".xlsx,.xls"
                                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                                    className="tw-block tw-w-full tw-text-xs tw-text-gray-700 file:tw-mr-3 file:tw-py-1.5 file:tw-px-3 file:tw-rounded file:tw-border-0 file:tw-bg-blue-50 file:tw-text-blue-700 hover:file:tw-bg-blue-100"
                                />
                                {uploadFile && (
                                    <p className="tw-text-[11px] tw-text-gray-500 tw-mt-1">
                                        Selected: {uploadFile.name} ({Math.round(uploadFile.size / 1024)} KB)
                                    </p>
                                )}
                            </label>
                        </div>
                        <div className="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-px-4 tw-py-3 tw-bg-gray-50 tw-border-t tw-border-gray-100">
                            <button
                                type="button"
                                onClick={() => { setUploadModalOpen(false); setUploadFile(null); }}
                                disabled={bulkUploadVendorPaymentsLoading}
                                className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-100 disabled:tw-opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleUploadPaymentSubmit}
                                disabled={!uploadFile || bulkUploadVendorPaymentsLoading}
                                className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700 tw-rounded-md disabled:tw-opacity-50"
                            >
                                {bulkUploadVendorPaymentsLoading && <Icon icon="mdi:loading" className="tw-animate-spin" />}
                                {bulkUploadVendorPaymentsLoading ? 'Uploading…' : 'Upload'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Excel Upload — results modal (payments) */}
            {uploadResultOpen && bulkUploadVendorPaymentsResult && (
                <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4">
                    <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-w-full tw-max-w-2xl tw-max-h-[85vh] tw-flex tw-flex-col">
                        <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-border-b tw-border-gray-100">
                            <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-flex tw-items-center tw-gap-2">
                                <Icon icon="mdi:file-check-outline" className="tw-text-emerald-600" />
                                Upload Summary
                            </h3>
                            <button
                                onClick={handleClosePaymentResults}
                                className="tw-p-1 tw-rounded hover:tw-bg-gray-100 tw-text-gray-500"
                            >
                                <Icon icon="mdi:close" />
                            </button>
                        </div>
                        <div className="tw-p-4 tw-overflow-y-auto tw-flex-1">
                            <div className="tw-grid tw-grid-cols-3 tw-gap-2 tw-mb-4">
                                <div className="tw-rounded-md tw-bg-blue-50 tw-border tw-border-blue-100 tw-px-3 tw-py-2">
                                    <div className="tw-text-[10px] tw-text-blue-700 tw-uppercase tw-tracking-wide tw-font-semibold">Total</div>
                                    <div className="tw-text-lg tw-font-bold tw-text-blue-900">{bulkUploadVendorPaymentsResult.total}</div>
                                </div>
                                <div className="tw-rounded-md tw-bg-emerald-50 tw-border tw-border-emerald-100 tw-px-3 tw-py-2">
                                    <div className="tw-text-[10px] tw-text-emerald-700 tw-uppercase tw-tracking-wide tw-font-semibold">Created</div>
                                    <div className="tw-text-lg tw-font-bold tw-text-emerald-900">{bulkUploadVendorPaymentsResult.createdCount}</div>
                                </div>
                                <div className="tw-rounded-md tw-bg-rose-50 tw-border tw-border-rose-100 tw-px-3 tw-py-2">
                                    <div className="tw-text-[10px] tw-text-rose-700 tw-uppercase tw-tracking-wide tw-font-semibold">Failed</div>
                                    <div className="tw-text-lg tw-font-bold tw-text-rose-900">{bulkUploadVendorPaymentsResult.failedCount}</div>
                                </div>
                            </div>

                            {bulkUploadVendorPaymentsResult.created?.length > 0 && (
                                <div className="tw-mb-4">
                                    <h4 className="tw-text-xs tw-font-semibold tw-text-emerald-700 tw-uppercase tw-tracking-wide tw-mb-1.5">Created</h4>
                                    <ul className="tw-space-y-0.5 tw-text-xs tw-text-gray-700 tw-max-h-40 tw-overflow-y-auto tw-border tw-border-gray-100 tw-rounded tw-p-2">
                                        {bulkUploadVendorPaymentsResult.created.map(r => (
                                            <li key={r.payment_id} className="tw-flex tw-items-center tw-gap-2">
                                                <span className="tw-text-gray-400 tw-font-mono tw-text-[10px] tw-w-8">#{r.row}</span>
                                                <Icon icon="mdi:check-circle" className="tw-text-emerald-500 tw-text-sm" />
                                                <span className="tw-font-medium">{r.vendor}</span>
                                                <span className="tw-text-gray-500">— ₹{r.amount}</span>
                                                {r.bill_number && <span className="tw-text-gray-400">(bill {r.bill_number})</span>}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {bulkUploadVendorPaymentsResult.failed?.length > 0 && (
                                <div>
                                    <h4 className="tw-text-xs tw-font-semibold tw-text-rose-700 tw-uppercase tw-tracking-wide tw-mb-1.5">Failed</h4>
                                    <ul className="tw-space-y-0.5 tw-text-xs tw-text-gray-700 tw-max-h-56 tw-overflow-y-auto tw-border tw-border-rose-100 tw-rounded tw-p-2">
                                        {bulkUploadVendorPaymentsResult.failed.map((r, i) => (
                                            <li key={i} className="tw-flex tw-items-start tw-gap-2">
                                                <span className="tw-text-gray-400 tw-font-mono tw-text-[10px] tw-w-8 tw-mt-0.5">#{r.row}</span>
                                                <Icon icon="mdi:alert-circle" className="tw-text-rose-500 tw-text-sm tw-mt-0.5 tw-flex-shrink-0" />
                                                <span>
                                                    {r.vendor && <span className="tw-font-medium">{r.vendor}: </span>}
                                                    <span className="tw-text-rose-700">{r.error}</span>
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                        <div className="tw-flex tw-items-center tw-justify-end tw-px-4 tw-py-3 tw-bg-gray-50 tw-border-t tw-border-gray-100">
                            <button
                                onClick={handleClosePaymentResults}
                                className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700 tw-rounded-md"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default VendorPayment;
